import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  AccessControl,
  CheckboxMui,
  ExpandedTable,
  MuiButton,
} from "@ryde/controls";
import {
  bookingBulkCreateStateFromPollingState,
  BookingCellId,
  BookingConflictErrorType,
  bulkApprovalBookingConflictsAtom,
  bulkApprovalBookingConflictsUpdatedAtom,
  DialogType,
  getBookingCalendarCellId,
  IApprovalBookingConflict,
  ReasonDtoDropdownItem,
  SimpleResult,
  useDialogs,
} from "@ryde/shared";

import { BulkPreviewStepConflictsContext } from "../bulk-preview-step-conflicts.context";
import { RejectedBookingExpanded } from "./rejected-bookings-expanded";
import { RejectedBookingsWithinProductionLockRow } from "./rejected-bookings-within-production-row";

export const RejectedBookingsWithinProductionLock: FC = () => {
  const setApprovalBookingConflictsUpdated = useSetRecoilState(
    bulkApprovalBookingConflictsUpdatedAtom,
  );
  const setApprovalBookingConflicts = useSetRecoilState(
    bulkApprovalBookingConflictsAtom,
  );
  const { conflicts } = useContext(BulkPreviewStepConflictsContext);
  const { openAsyncDialog } = useDialogs();
  const { isLoading } = useRecoilValue(bookingBulkCreateStateFromPollingState);
  const { t } = useTranslation();

  const [
    selectedBookingsWithinProductionLockIds,
    setSelectedBookingsWithinProductionLockIds,
  ] = useState<Map<BookingCellId, boolean>>(new Map());

  const { allCellsIdsAsSelected, allCellsIdsAsUnSelected } = useMemo(() => {
    const initAllAsSelected = new Map<BookingCellId, true>();
    const initAllAsUnSelected = new Map<BookingCellId, false>();

    if (!isLoading) {
      conflicts?.forEach(({ date, passengerId, errorType }) => {
        if (errorType === BookingConflictErrorType.withinProductionLock) {
          const cellId = getBookingCalendarCellId(passengerId, date);
          initAllAsSelected.set(cellId, true);
          initAllAsUnSelected.set(cellId, false);
        }
      });
    }

    return {
      allCellsIdsAsSelected: initAllAsSelected,
      allCellsIdsAsUnSelected: initAllAsUnSelected,
    };
  }, [conflicts, isLoading]);

  useEffect(() => {
    if (
      !isLoading &&
      allCellsIdsAsUnSelected.size !==
        selectedBookingsWithinProductionLockIds.size
    ) {
      setSelectedBookingsWithinProductionLockIds(allCellsIdsAsUnSelected);
    }
  }, [
    allCellsIdsAsUnSelected,
    isLoading,
    selectedBookingsWithinProductionLockIds.size,
  ]);

  const isAllSelected = useMemo((): boolean => {
    return (
      selectedBookingsWithinProductionLockIds
        ?.values()
        ?.every(value => value) ?? false
    );
  }, [selectedBookingsWithinProductionLockIds]);

  const toggleCheckbox = (cellId: BookingCellId): void => {
    if (selectedBookingsWithinProductionLockIds.has(cellId)) {
      const temp = new Map(selectedBookingsWithinProductionLockIds);

      const isCellSelected = temp.get(cellId);

      if (isCellSelected) {
        temp.set(cellId, false);
      } else {
        temp.set(cellId, true);
      }

      setSelectedBookingsWithinProductionLockIds(temp);
    }
  };

  const toggleSelectAllCheckbox = (): void => {
    if (isAllSelected) {
      setSelectedBookingsWithinProductionLockIds(allCellsIdsAsUnSelected);
    } else {
      setSelectedBookingsWithinProductionLockIds(allCellsIdsAsSelected);
    }
  };

  const setter = useEvent(
    ({
      prevState,
      approvalResult,
      cellId,
    }: {
      approvalResult: SimpleResult<{
        approvalReason: ReasonDtoDropdownItem;
        approver: string;
      }>;
      prevState: Map<BookingCellId, IApprovalBookingConflict[]>;
      cellId: BookingCellId;
    }): Map<BookingCellId, IApprovalBookingConflict[]> => {
      const prevApprovalBookingConflicts = prevState.get(
        cellId,
      ) as IApprovalBookingConflict[];

      const approvalBookingConflicts = prevApprovalBookingConflicts.map(
        conflict => ({
          ...conflict,
          approvalReason: approvalResult.data
            ?.approvalReason as ReasonDtoDropdownItem,
          approver: approvalResult.data?.approver as string,
        }),
      );

      return new Map([...prevState.set(cellId, approvalBookingConflicts)]);
    },
  );

  const onResolveConflictClick = async (): Promise<void> => {
    const approvalResult = await openAsyncDialog<{
      approvalReason: ReasonDtoDropdownItem;
      approver: string;
    }>({
      dialogName:
        DialogType.BookingBulkSetConflictApprovalReasonMultiPassengers,
      dialogProps: {
        bookingCellsIds: Array.from(
          selectedBookingsWithinProductionLockIds.keys(),
        ),
      },
    });

    if (!approvalResult.success) {
      return;
    }

    selectedBookingsWithinProductionLockIds.forEach((isSelected, cellId) => {
      if (isSelected) {
        setApprovalBookingConflictsUpdated(prevState =>
          setter({ prevState, approvalResult, cellId }),
        );

        setApprovalBookingConflicts(prevState =>
          setter({ prevState, approvalResult, cellId }),
        );
      }
    });
  };

  const isSaveCanBeApply = useMemo(() => {
    return (
      selectedBookingsWithinProductionLockIds?.values()?.some(val => val) ??
      false
    );
  }, [selectedBookingsWithinProductionLockIds]);

  return (
    <RejectedBookingExpanded>
      <ExpandedTable dataE2E="rejected-bookings-with-production-lock">
        <Table size="small">
          <TableHead>
            <TableRow>
              <AccessControl
                entity="booking"
                action="approve_conflict_within_production_lock"
              >
                {!isLoading && (
                  <TableCell>
                    <CheckboxMui
                      checked={isAllSelected}
                      onCheckboxToggle={toggleSelectAllCheckbox}
                    />
                  </TableCell>
                )}
              </AccessControl>
              <TableCell>{t("booking.bulk.passengerId")}</TableCell>
              <TableCell>{t("booking.bulk.passengerFullName")}</TableCell>
              <TableCell>{t("booking.bulk.date")}</TableCell>
              <TableCell>{t("general.DIRECTION")}</TableCell>
              <AccessControl
                entity="booking"
                action="approve_conflict_within_production_lock"
              >
                <TableCell>
                  <MuiButton
                    e2eData={`save_button_right_footer`}
                    muiStyle={!isSaveCanBeApply ? "error" : "success"}
                    disabled={!isSaveCanBeApply}
                    onClick={onResolveConflictClick}
                  >
                    {t(`general.APPLY`)}
                  </MuiButton>
                </TableCell>
              </AccessControl>
            </TableRow>
          </TableHead>
          <TableBody>
            {conflicts?.map((conflictRow, i) => (
              <RejectedBookingsWithinProductionLockRow
                handleCheckboxChange={toggleCheckbox}
                key={`${getBookingCalendarCellId(conflictRow.passengerId as number, conflictRow.date)}_${i}`}
                conflictRow={conflictRow}
                checked={selectedBookingsWithinProductionLockIds.get(
                  getBookingCalendarCellId(
                    conflictRow.passengerId as number,
                    conflictRow.date,
                  ),
                )}
                isLoading={isLoading}
              />
            ))}
          </TableBody>
        </Table>
      </ExpandedTable>
    </RejectedBookingExpanded>
  );
};
